import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import inventoryApi from '../../../services/inventoryApi';
import adminApi from '../../../services/adminApi';
import LoadingSpinner from '../../Common/LoadingSpinner';

const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'LKR',
  maximumFractionDigits: 0,
}).format(amount || 0);

const formatNumber = (value) => (value ?? 0).toLocaleString();
const formatPercent = (value) => (value === null || value === undefined ? '—' : `${value.toFixed(1)}%`);

const ALERT_STYLES = {
  HIGH: 'bg-red-50 text-red-800 border border-red-100',
  MEDIUM: 'bg-yellow-50 text-yellow-800 border border-yellow-100',
};

const PO_STATUS_COLORS = {
  draftOrders: '#9ca3af',
  pendingOrders: '#ca8a04',
  sentOrders: '#2563eb',
  partialOrders: '#9333ea',
  completedOrders: '#16a34a',
  cancelledOrders: '#dc2626',
};
const PO_STATUS_LABELS = {
  draftOrders: 'Draft',
  pendingOrders: 'Pending',
  sentOrders: 'Sent',
  partialOrders: 'Partial',
  completedOrders: 'Completed',
  cancelledOrders: 'Cancelled',
};

const MODULE_GROUPS = [
  {
    title: 'Procurement',
    links: [
      { label: 'Purchase Requests', href: '/inventory/purchase-requests' },
      { label: 'Purchase Orders', href: '/inventory/purchase-orders' },
      { label: 'Goods Received Notes', href: '/inventory/grn' },
      { label: 'Supplier Payments', href: '/inventory/supplier-payments' },
    ],
  },
  {
    title: 'Stock Operations',
    links: [
      { label: 'Stock Management', href: '/inventory/stock' },
      { label: 'Goods Transfer Requests', href: '/inventory/goods-transfer-requests' },
      { label: 'Goods Issue Notes', href: '/inventory/goods-issue-notes' },
      { label: 'Transfer In', href: '/inventory/transfer-in' },
    ],
  },
  {
    title: 'Consumption',
    links: [
      { label: 'Material Request Notes', href: '/inventory/material-request-notes' },
      { label: 'Inventory Consumption', href: '/inventory/inventory-consumption' },
    ],
  },
  {
    title: 'Master Data',
    links: [
      { label: 'Items', href: '/inventory/items' },
      { label: 'Categories', href: '/inventory/categories' },
      { label: 'Suppliers', href: '/inventory/suppliers' },
      { label: 'Return Types', href: '/inventory/return-types' },
    ],
  },
  {
    title: 'Sales & Pricing',
    links: [
      { label: 'Pricing', href: '/inventory/pricing' },
      { label: 'Sales Channels', href: '/inventory/sales-channels' },
      { label: 'Taxes', href: '/inventory/taxes' },
      { label: 'Distribution Routes', href: '/inventory/distribution-routes' },
    ],
  },
  {
    title: 'Reports',
    links: [
      { label: 'Stock Levels', href: '/inventory/reports/stock-levels' },
      { label: 'Stock Valuation', href: '/inventory/reports/valuation' },
      { label: 'Stock Movements', href: '/inventory/reports/stock-movements' },
      { label: 'Bincard', href: '/inventory/reports/bincard' },
      { label: 'Analytics', href: '/inventory/reports/analytics' },
    ],
  },
];

const CustomBarTooltip = ({ active, payload, labelKey = 'categoryName' }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm">
        <p className="font-medium text-gray-900">{payload[0].payload[labelKey]}</p>
        <p className="text-gray-600">{payload[0].payload.formatted || formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

const timeAgo = (timestamp) => {
  if (!timestamp) return '';
  const diffMs = Date.now() - new Date(timestamp).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const StatCard = ({ label, value, sub, color }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-xl font-semibold text-gray-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
    </div>
    <div className={`h-1 w-10 rounded-full mt-4 bg-${color}-500`} />
  </div>
);

const RatioTile = ({ label, value, description }) => (
  <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    <p className="text-xs text-gray-400 mt-1">{description}</p>
  </div>
);

const SectionCard = ({ title, subtitle, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-400 mb-4">{subtitle}</p>}
    {children}
  </div>
);

const HorizontalBarChart = ({ data, height, labelKey = 'categoryName' }) => (
  <ResponsiveContainer width="100%" height={height || Math.max(160, data.length * 40)}>
    <BarChart data={data} layout="vertical" margin={{ left: 0, right: 28, top: 4, bottom: 4 }}>
      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f3f4f6" />
      <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
      <YAxis type="category" dataKey={labelKey} width={110} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
      <Tooltip content={<CustomBarTooltip labelKey={labelKey} />} cursor={{ fill: '#f9fafb' }} />
      <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
        {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

const EMPTY_DATA = {
  kpis: {},
  charts: { topCategories: [], stockByWarehouse: [] },
  alerts: [],
  recentActivities: [],
  procurement: {},
};

const InventoryDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dashboardData, setDashboardData] = useState(EMPTY_DATA);
  const [warehouseMap, setWarehouseMap] = useState({});

  useEffect(() => {
    loadDashboardData();
    loadWarehouses();
  }, []);

  const loadWarehouses = async () => {
    try {
      const res = await adminApi.getLocations({ limit: 1000 });
      const data = res.data?.data || res.data || [];
      const map = {};
      data.forEach((loc) => { map[loc._id] = loc.name; });
      setWarehouseMap(map);
    } catch {
      setWarehouseMap({});
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const res = await inventoryApi.getDashboardData();
      setDashboardData(res?.data || EMPTY_DATA);
    } catch (err) {
      console.error('Error loading inventory dashboard data:', err);
      toast.error(err?.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const { kpis = {}, charts = {}, alerts = [], recentActivities = [], procurement = {} } = dashboardData;
  const topCategories = (charts.topCategories || []).slice(0, 8).map((c) => ({
    categoryName: c.categoryName, value: c.totalValue, color: '#4f46e5',
  }));

  const stockByWarehouse = useMemo(() => (charts.stockByWarehouse || []).slice(0, 8).map((w) => ({
    categoryName: warehouseMap[w.warehouseId] || 'Unknown',
    value: w.totalValue,
    color: '#0ea5e9',
  })), [charts.stockByWarehouse, warehouseMap]);

  const poStats = procurement.purchaseOrders || {};
  const poStatusChart = Object.keys(PO_STATUS_LABELS).map((key) => ({
    categoryName: PO_STATUS_LABELS[key],
    value: poStats[key] || 0,
    color: PO_STATUS_COLORS[key],
  })).filter((d) => d.value > 0);

  const orderFulfillmentPct = poStats.totalOrders > 0
    ? Math.round((poStats.completedOrders / poStats.totalOrders) * 1000) / 10
    : null;
  const lowStockRatePct = kpis.totalItems > 0
    ? Math.round((kpis.lowStockAlert / kpis.totalItems) * 1000) / 10
    : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory Dashboard</h1>
          <p className="text-xs text-gray-500 mt-1">Last {dashboardData.period ?? 30} days</p>
        </div>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-md bg-red-50 text-red-800 text-sm">{error}</div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Alerts & Notifications</h3>
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-md ${ALERT_STYLES[alert.urgency] || 'bg-blue-50 text-blue-800 border border-blue-100'}`}
                >
                  {alert.message}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Headline KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard label="Total Stock Value" value={formatCurrency(kpis.totalValue)} color="green" />
        <StatCard label="Total Items" value={formatNumber(kpis.totalItems)} color="blue" />
        <StatCard label="Low Stock Alerts" value={formatNumber(kpis.lowStockAlert)} color="yellow" />
        <StatCard label={`Movements (${dashboardData.period ?? 30}d)`} value={formatNumber(kpis.totalMovements)} sub={`${formatNumber(kpis.inboundMovements)} in / ${formatNumber(kpis.outboundMovements)} out`} color="purple" />
      </div>

      {/* Standard inventory ratios */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <RatioTile label="Inventory Turnover" value={kpis.averageTurnover ? `${kpis.averageTurnover}×` : '—'} description="Annualized units sold / avg stock" />
        <RatioTile label="Days Inventory Outstanding" value={kpis.daysInventoryOutstanding ? `${kpis.daysInventoryOutstanding}d` : '—'} description="365 / turnover ratio" />
        <RatioTile label="Order Fulfillment" value={formatPercent(orderFulfillmentPct)} description="Completed / total purchase orders" />
        <RatioTile label="Low Stock Rate" value={formatPercent(lowStockRatePct)} description="Items below reorder point" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stock Valuation */}
        <SectionCard title="Top Categories by Value" subtitle="Current stock value by category">
          {topCategories.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No stock data available</div>
          ) : (
            <HorizontalBarChart data={topCategories} />
          )}
        </SectionCard>

        {/* Stock by Warehouse */}
        <SectionCard title="Stock by Warehouse" subtitle="Current stock value by location">
          {stockByWarehouse.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No stock data available</div>
          ) : (
            <HorizontalBarChart data={stockByWarehouse} />
          )}
        </SectionCard>

        {/* Procurement Pipeline */}
        <SectionCard title="Procurement Pipeline" subtitle="Purchase orders, requests and receipts">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-xs text-blue-700">Purchase Orders</p>
              <p className="text-sm font-semibold text-blue-900">{formatNumber(poStats.totalOrders)}</p>
              <p className="text-xs text-blue-700 mt-1">{formatCurrency(poStats.totalValue)}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50">
              <p className="text-xs text-amber-700">Purchase Requests</p>
              <p className="text-sm font-semibold text-amber-900">{formatNumber(procurement.purchaseRequests?.metrics?.totalRequests)}</p>
              <p className="text-xs text-amber-700 mt-1">{formatNumber(procurement.purchaseRequests?.metrics?.pendingApprovalsCount)} pending approval</p>
            </div>
            <div className="p-3 rounded-lg bg-teal-50">
              <p className="text-xs text-teal-700">Goods Received Notes</p>
              <p className="text-sm font-semibold text-teal-900">{formatNumber(procurement.grn?.totalGRNs)}</p>
              <p className="text-xs text-teal-700 mt-1">{formatNumber(procurement.grn?.pendingInspections)} pending inspection</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50">
              <p className="text-xs text-purple-700">Active Suppliers</p>
              <p className="text-sm font-semibold text-purple-900">{formatNumber(procurement.supplierCount)}</p>
            </div>
          </div>
          {poStatusChart.length === 0 ? (
            <div className="h-24 flex items-center justify-center text-gray-400 text-sm">No purchase orders yet</div>
          ) : (
            <HorizontalBarChart data={poStatusChart} height={Math.max(120, poStatusChart.length * 36)} />
          )}
        </SectionCard>

        {/* Recent Activities */}
        <SectionCard title="Recent Activity" subtitle="Latest stock movements">
          {recentActivities.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">No recent activity</div>
          ) : (
            <ul className="space-y-3 max-h-72 overflow-y-auto">
              {recentActivities.map((activity) => (
                <li key={activity.id} className="flex items-start justify-between gap-3 text-sm border-b border-gray-100 pb-2 last:border-0">
                  <div>
                    <p className="text-gray-800">{activity.description}</p>
                    <p className="text-xs text-gray-400">{activity.warehouse} &middot; {activity.user}</p>
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{timeAgo(activity.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      {/* Inventory Modules Overview */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Inventory Modules</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODULE_GROUPS.map((group) => (
              <div key={group.title} className="border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-2">{group.title}</h4>
                <div className="space-y-1">
                  {group.links.map((link) => (
                    <button
                      key={link.label}
                      onClick={() => navigate(link.href)}
                      className="block w-full text-left text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryDashboard;
